import type { ParsedCvData } from "@tabbio/types";
import type { CVProfile } from "./cv-mock-data";
import { composeEducationDescription } from "@tabbio/utils/education-description";
import {
  assessCvCompleteness,
  type CvCompletenessAssessment,
} from "@tabbio/utils/cv-body-evidence";
import {
  mapCertificationToAPI,
  mapEducationToAPI,
  mapExperienceToAPI,
  mapHighlightToAPI,
  mapLanguagesToAPI,
  mapParsedToCVProfile,
} from "./cv-mapping";

export function buildCvProfileReplacementFromParsed(
  parsed: ParsedCvData,
): Partial<CVProfile> {
  const mapped = mapParsedToCVProfile(parsed);

  const replacement: Partial<CVProfile> = {
    headline: mapped.headline ?? "",
    location: mapped.location ?? "",
    contactEmail: mapped.contactEmail ?? "",
    phone: mapped.phone ?? "",
    website: mapped.website ?? "",
    linkedin: mapped.linkedin ?? "",
    github: mapped.github ?? "",
    dribbble: mapped.dribbble ?? "",
    behance: mapped.behance ?? "",
    summary: mapped.summary ?? "",
    highlights: mapped.highlights ?? [],
    skillGroups: mapped.skillGroups ?? [],
    experience: mapped.experience ?? [],
    education: mapped.education ?? [],
    projects: mapped.projects ?? [],
    certifications: mapped.certifications ?? [],
    languages: mapped.languages ?? [],
  };

  if (mapped.name?.trim()) {
    replacement.name = mapped.name;
  }

  return replacement;
}

export function buildSaveSectionsFromCvProfile(
  profile: Partial<CVProfile>,
  options?: {
    /**
     * Authorize the save against the server's replacement guard, which rejects
     * every save-sections call once the account already owns a CV row
     * (onboarding.service.ts). Claiming a CV handoff during onboarding CREATES
     * that row, so without this the claim flow's own preview editors can never
     * save what the user came to edit.
     *
     * Deliberately NOT `replaceExisting`: that flag is the destructive
     * full-import switch (it overwrites the ResumeSource revert snapshot and
     * lets an empty array wipe a section). This one only says "yes, write over
     * the CV I am looking at", which is precisely the intent of an edit made
     * from the preview.
     */
    replacementConfirmed?: boolean;
  },
) {
  const sections: Record<string, unknown> = {};

  if (
    profile.summary ||
    profile.headline ||
    profile.location ||
    profile.contactEmail ||
    profile.phone ||
    profile.skillGroups?.length ||
    profile.linkedin ||
    profile.website ||
    profile.github ||
    profile.dribbble ||
    profile.behance
  ) {
    sections.profile = {
      title: profile.headline,
      summary: profile.summary,
      location: profile.location,
      contactEmail: profile.contactEmail,
      phone: profile.phone,
      skills: profile.skillGroups?.flatMap(
        (group: any) => group.items || group.skills || [],
      ),
      socials: {
        linkedin: profile.linkedin,
        website: profile.website,
        github: profile.github,
        dribbble: profile.dribbble,
        behance: profile.behance,
      },
    };
  }

  if (profile.experience?.length) {
    sections.experience = profile.experience.map(mapExperienceToAPI);
  }

  if (profile.education?.length) {
    sections.education = profile.education.map(mapEducationToAPI);
  }

  if (profile.projects?.length) {
    sections.projects = profile.projects.map((project, order) => ({
      ...project,
      order,
    }));
  }

  if (profile.certifications?.length) {
    sections.certifications = profile.certifications.map(mapCertificationToAPI);
  }

  if (profile.highlights?.length) {
    sections.highlights = profile.highlights.map(mapHighlightToAPI);
  }

  if (profile.languages?.length) {
    sections.languages = mapLanguagesToAPI(profile.languages);
  }

  // Last, and only alongside real content: callers skip the request entirely
  // when this object is empty, and a payload carrying nothing but the flag
  // would defeat that check and POST an empty save.
  if (options?.replacementConfirmed && Object.keys(sections).length > 0) {
    sections.replacementConfirmed = true;
  }

  return sections;
}

/**
 * Thrown when a caller asks for a destructive `replaceExisting` save with a
 * parse that has no CV body (incident 2026-09-02: a provider-failed import
 * came back as a contact header and replaced a full profile with empty
 * sections). The server refuses the same payload; this keeps the client from
 * ever sending it.
 */
export class ImportedCvIncompleteError extends Error {
  readonly code = "CV_PARSE_INCOMPLETE";

  constructor(readonly assessment: CvCompletenessAssessment) {
    super(
      "This import has no work experience, education, certifications, projects, or highlights, so it cannot replace your CV.",
    );
    this.name = "ImportedCvIncompleteError";
  }
}

export function isImportedCvIncompleteError(
  error: unknown,
): error is ImportedCvIncompleteError {
  return (
    error instanceof ImportedCvIncompleteError ||
    (typeof error === "object" &&
      error !== null &&
      (error as { name?: unknown }).name === "ImportedCvIncompleteError")
  );
}

/**
 * Whether a parsed CV carries enough content to stand in for the user's CV:
 * a body record, or a real summary / skills list. A contact header alone never
 * does. Shared with the API through `@tabbio/utils/cv-body-evidence`.
 */
export function assessParsedCvForReplacement(parsed: ParsedCvData) {
  return assessCvCompleteness(parsed);
}

export function buildSaveSectionsFromParsedCvData(
  parsed: ParsedCvData,
  options?: { replaceExisting?: boolean },
) {
  const sections: Record<string, unknown> = {};
  const replaceExisting = options?.replaceExisting === true;

  if (replaceExisting) {
    // Never build the destructive payload for a parse that is not a CV.
    const assessment = assessCvCompleteness(parsed);
    if (!assessment.hasMeaningfulEvidence) {
      throw new ImportedCvIncompleteError(assessment);
    }
    sections.replaceExisting = true;
  }

  if (
    replaceExisting ||
    parsed.name ||
    parsed.title ||
    parsed.summary ||
    parsed.location ||
    parsed.email ||
    parsed.phone ||
    parsed.skills?.length ||
    parsed.socials
  ) {
    const mapped = mapParsedToCVProfile(parsed);

    const profileSection: Record<string, unknown> = {
      title: parsed.title ?? "",
      summary: parsed.summary ?? "",
      location: parsed.location ?? "",
      contactEmail: parsed.email ?? "",
      phone: mapped.phone ?? "",
      skills: parsed.skills ?? [],
      skillGroups: mapped.skillGroups ?? [],
      socials: {
        ...(parsed.socials ?? {}),
        ...(mapped.linkedin ? { linkedin: mapped.linkedin } : {}),
        ...(mapped.website ? { website: mapped.website } : {}),
      },
    };

    if (parsed.name?.trim()) {
      profileSection.name = parsed.name;
    }

    sections.profile = profileSection;
  }

  if (replaceExisting || parsed.experience?.length) {
    sections.experience = (parsed.experience ?? []).map(
      (experience, index) => ({
        company: experience.company,
        title: experience.title,
        location: experience.location,
        locationType: experience.locationType,
        employmentType: experience.employmentType,
        startDate: experience.startDate,
        endDate: experience.endDate,
        current: experience.current,
        description: experience.description,
        achievements: experience.achievements,
        order: index,
      }),
    );
  }

  if (replaceExisting || parsed.education?.length) {
    sections.education = (parsed.education ?? []).map((education, index) => ({
      school: education.school,
      degree: education.degree,
      fieldOfStudy: education.fieldOfStudy,
      startDate: education.startDate,
      endDate: education.endDate,
      grade: education.grade,
      description:
        composeEducationDescription({
          description: education.description,
          details: education.details,
          grade: education.grade,
        }) || undefined,
      order: index,
    }));
  }

  if (replaceExisting || parsed.certifications?.length) {
    sections.certifications = (parsed.certifications ?? []).map(
      (certification, index) => ({
        name: certification.name,
        issuer: certification.issuer,
        issueDate: certification.issueDate,
        expirationDate: certification.expirationDate,
        credentialId: certification.credentialId,
        credentialUrl: certification.credentialUrl,
        order: index,
      }),
    );
  }

  if (replaceExisting || parsed.careerHighlights?.length) {
    sections.highlights = (parsed.careerHighlights ?? []).map(
      (highlight, index) => ({
        title: highlight.title,
        description: highlight.description,
        order: index,
      }),
    );
  }

  if (replaceExisting || parsed.featuredProjects?.length) {
    sections.projects = (parsed.featuredProjects ?? []).map(
      (project, index) => ({
        title: project.title,
        description: project.description,
        link: project.link,
        technologies: project.technologies,
        order: index,
      }),
    );
  }

  if (replaceExisting || parsed.languages?.length) {
    sections.languages = mapLanguagesToAPI(parsed.languages ?? []);
  }

  return sections;
}
